import { ref } from 'vue'
import type { User } from '@/types'
import { telegramWebApp } from '@/utils/telegram'

export interface AuthDebugEvent {
    timestamp: number
    type: 'init' | 'login' | 'logout' | 'check' | 'clear' | 'save' | 'load' | 'error' | 'refresh' | 'sync'
    message: string
    data?: unknown
}

export interface AuthDebugInfo {
    currentUser: User | null
    telegramUser: {
        id?: number
        username?: string
        first_name?: string
    } | null
    userMatch: boolean | null
    hasToken: boolean
    hasRefreshToken: boolean
    tokenPreview: string
    refreshTokenPreview: string
    localStorageUser: User | null
    localStorageToken: string | null
    localStorageRefreshToken: string | null
    events: AuthDebugEvent[]
    errors: string[]
}

// Глобальное состояние для дебага
const debugInfo = ref<AuthDebugInfo>({
    currentUser: null,
    telegramUser: null,
    userMatch: null,
    hasToken: false,
    hasRefreshToken: false,
    tokenPreview: '',
    refreshTokenPreview: '',
    localStorageUser: null,
    localStorageToken: null,
    localStorageRefreshToken: null,
    events: [],
    errors: [],
})

const MAX_EVENTS = 100 // Максимальное количество событий в истории

// Глобальные функции для логирования (можно вызывать из любого места)
export const authDebug = {
    addEvent(type: AuthDebugEvent['type'], message: string, data?: unknown) {
        const event: AuthDebugEvent = {
            timestamp: Date.now(),
            type,
            message,
            data,
        }
        debugInfo.value.events.unshift(event)
        
        if (debugInfo.value.events.length > MAX_EVENTS) {
            debugInfo.value.events = debugInfo.value.events.slice(0, MAX_EVENTS)
        }
    },
    
    addError(error: string) {
        debugInfo.value.errors.unshift(error)
        authDebug.addEvent('error', error)
        
        if (debugInfo.value.errors.length > 50) {
            debugInfo.value.errors = debugInfo.value.errors.slice(0, 50)
        }
    },
    
    updateCurrentUser(user: User | null) {
        debugInfo.value.currentUser = user
    },
    
    updateTelegramUser() {
        const telegramUser = telegramWebApp.getUser()
        debugInfo.value.telegramUser = telegramUser
            ? {
                  id: telegramUser.id,
                  username: telegramUser.username,
                  first_name: telegramUser.first_name,
              }
            : null
    },
    
    updateUserMatch(match: boolean | null) {
        debugInfo.value.userMatch = match
    },
    
    updateTokens(token: string | null, refreshToken: string | null) {
        debugInfo.value.hasToken = !!token
        debugInfo.value.hasRefreshToken = !!refreshToken
        
        if (token) {
            const previewLength = 8
            if (token.length > previewLength * 2) {
                debugInfo.value.tokenPreview = `${token.substring(0, previewLength)}...${token.substring(token.length - previewLength)}`
            } else {
                debugInfo.value.tokenPreview = token.substring(0, previewLength) + '...'
            }
        } else {
            debugInfo.value.tokenPreview = ''
        }
        
        if (refreshToken) {
            const previewLength = 8
            if (refreshToken.length > previewLength * 2) {
                debugInfo.value.refreshTokenPreview = `${refreshToken.substring(0, previewLength)}...${refreshToken.substring(refreshToken.length - previewLength)}`
            } else {
                debugInfo.value.refreshTokenPreview = refreshToken.substring(0, previewLength) + '...'
            }
        } else {
            debugInfo.value.refreshTokenPreview = ''
        }
    },
    
    updateLocalStorageInfo() {
        try {
            const token = localStorage.getItem('access_token')
            const refreshToken = localStorage.getItem('refresh_token')
            const userStr = localStorage.getItem('user')
            
            debugInfo.value.localStorageToken = token
            debugInfo.value.localStorageRefreshToken = refreshToken
            
            if (userStr) {
                try {
                    debugInfo.value.localStorageUser = JSON.parse(userStr)
                } catch {
                    debugInfo.value.localStorageUser = null
                }
            } else {
                debugInfo.value.localStorageUser = null
            }
        } catch (error) {
            authDebug.addError(`Failed to read localStorage: ${error}`)
        }
    },
    
    refreshAll() {
        authDebug.updateTelegramUser()
        authDebug.updateLocalStorageInfo()
        
        if (debugInfo.value.currentUser && debugInfo.value.telegramUser) {
            const match = debugInfo.value.currentUser.telegram_id === debugInfo.value.telegramUser.id
            authDebug.updateUserMatch(match)
        } else {
            authDebug.updateUserMatch(null)
        }
    },
    
    clearEvents() {
        debugInfo.value.events = []
        debugInfo.value.errors = []
    },
}

export function useAuthDebug() {
    return {
        debugInfo,
        addEvent: authDebug.addEvent,
        addError: authDebug.addError,
        updateCurrentUser: authDebug.updateCurrentUser,
        updateTelegramUser: authDebug.updateTelegramUser,
        updateUserMatch: authDebug.updateUserMatch,
        updateTokens: authDebug.updateTokens,
        updateLocalStorageInfo: authDebug.updateLocalStorageInfo,
        refreshAll: authDebug.refreshAll,
        clearEvents: authDebug.clearEvents,
    }
}

